import getStore from '../store/store';
import { mutator } from 'satcheljs';
import { ObservableMap } from 'mobx';
import {
    initializeCalendarsCacheInStore,
    addToCalendarsCache,
    addCalendarGroupToCalendarsCache,
    removeCalendarFromCalendarsCache,
    removeCalendarWithIDFromCalendarsCache,
} from '../actions/publicActions';
import { getFolderIdByCalendarID } from '../selectors/calendarsCacheSelectors';
import getCalendarGroupKey from '../utils/getCalendarGroupKey';

function addEntries<T>(map: ObservableMap<string, T>, obj: { [key: string]: T }) {
    Object.keys(obj).forEach(key => map.set(key, obj[key]));
}

mutator(initializeCalendarsCacheInStore, ({ defaultCalendarEntry }) => {
    if (defaultCalendarEntry) {
        getStore().defaultCalendars.set(
            defaultCalendarEntry.id.mailboxInfo.primarySmtpAddress,
            defaultCalendarEntry
        );
    }
});

mutator(initializeCalendarsCacheInStore, ({ calendarEntries }) => {
    addEntries(getStore().calendarEntryMapping, calendarEntries);
});

mutator(initializeCalendarsCacheInStore, ({ calendarFolders }) => {
    addEntries(getStore().calendarFolderMapping, calendarFolders);
});

mutator(initializeCalendarsCacheInStore, ({ folderIdMapping }) => {
    addEntries(getStore().folderIdToCalendarId, folderIdMapping);
});

mutator(initializeCalendarsCacheInStore, ({ calendarIdToChangeKeyMapping }) => {
    addEntries(getStore().calendarItemIdToChangeKey, calendarIdToChangeKeyMapping);
});

mutator(initializeCalendarsCacheInStore, ({ calendarGroups }) => {
    addEntries(getStore().calendarGroupsMapping, calendarGroups);
});

mutator(addToCalendarsCache, actionMessage => {
    const { calendarEntry, folder, mailboxInfo } = actionMessage;

    const store = getStore();

    store.calendarItemIdToChangeKey.set(calendarEntry.ItemId.Id, calendarEntry.ItemId.ChangeKey);

    let calendarEntryEx = {
        ...calendarEntry,
        id: { id: calendarEntry.ItemId.Id, mailboxInfo: mailboxInfo },
    };
    store.calendarEntryMapping.set(calendarEntry.ItemId.Id, calendarEntryEx);
    store.calendarFolderMapping.set(calendarEntry.ItemId.Id, folder);

    // When we don't have the proper FolderId for the Group Mailbox or linked calendar, we will read the DistinguishedFolderId.
    if (folder.FolderId) {
        store.folderIdToCalendarId.set(folder.FolderId.Id, {
            id: calendarEntry.ItemId.Id,
            mailboxInfo: mailboxInfo,
        });
    } else if (folder.DistinguishedFolderId) {
        store.folderIdToCalendarId.set(folder.DistinguishedFolderId, {
            id: calendarEntry.ItemId.Id,
            mailboxInfo: mailboxInfo,
        });
    }
});

mutator(addCalendarGroupToCalendarsCache, actionMessage => {
    const { calendarGroup } = actionMessage;

    const store = getStore();

    const groupKey = getCalendarGroupKey(
        calendarGroup.id.mailboxInfo.primarySmtpAddress,
        calendarGroup.GroupId
    );

    store.calendarGroupsMapping.set(groupKey, calendarGroup);
});

mutator(removeCalendarFromCalendarsCache, actionMessage => {
    const { calendar } = actionMessage;
    const calendarId = calendar.id.id;
    const folderId = calendar.calculatedFolderId ? calendar.calculatedFolderId : null;
    removeCalendarWithId(calendarId, folderId);
});

mutator(removeCalendarWithIDFromCalendarsCache, actionMessage => {
    const { calendarId } = actionMessage;
    const folderId = getFolderIdByCalendarID(calendarId);
    removeCalendarWithId(calendarId, folderId);
});

function removeCalendarWithId(calendarId: string, folderId?: string) {
    const store = getStore();

    store.calendarItemIdToChangeKey.delete(calendarId);
    store.calendarEntryMapping.delete(calendarId);
    store.calendarFolderMapping.delete(calendarId);
    if (folderId) {
        store.folderIdToCalendarId.delete(folderId);
    }
}
